import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  AfterViewInit,
} from '@angular/core';
import { NbDialogRef } from '@nebular/theme';
import { Subject, takeUntil } from 'rxjs';
import ApexCharts from 'apexcharts';
import {
  EquipmentCountingMetricsService,
  EquipmentCountingJournalMetricsFilter,
  LostEquipmentDetails,
  LostEquipmentLocationDetail,
  LostEquipmentCategoryDetail,
} from '../equipment-counting-metrics/equipment-counting-metrics.service';

@Component({
  selector: 'stack-mono-not-found-equipment-details-modal',
  templateUrl: './not-found-equipment-details-modal.component.html',
  styleUrls: ['./not-found-equipment-details-modal.component.scss'],
  standalone: false,
})
export class NotFoundEquipmentDetailsModalComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() filters: EquipmentCountingJournalMetricsFilter = {};
  @ViewChild('treemapContainer') treemapContainer: ElementRef;

  private destroy$ = new Subject<void>();

  // Data - reusing the same LostEquipmentDetails type since the structure is identical
  notFoundEquipmentDetails: LostEquipmentDetails | null = null;
  isLoading = false;
  error: string | null = null;

  // View state - Book Value or Replacement Cost toggle
  displayMode: 'bookValue' | 'replacementCost' = 'bookValue';
  expandedLocations: Set<string> = new Set();

  // Treemap chart
  private treemapChart: ApexCharts | null = null;

  // Treemap colors using the specified color scheme
  private treemapColors = [
    '#42A5F5', // Blue (graphs)
    '#2dd36f', // Green
    '#ff3d71', // Red
    '#FDD835', // Yellow
    '#ffa726', // Orange
    '#673AB7', // Purple
    '#92949C', // Gray
    '#0e4aff', // Blue (metrics)
  ];

  constructor(
    protected dialogRef: NbDialogRef<NotFoundEquipmentDetailsModalComponent>,
    private metricsService: EquipmentCountingMetricsService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  // eslint-disable-next-line @angular-eslint/no-empty-lifecycle-method, @typescript-eslint/no-empty-function
  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.treemapChart) {
      this.treemapChart.destroy();
    }
  }

  /**
   * Load not found equipment details data
   */
  private loadData(): void {
    this.isLoading = true;
    this.error = null;

    this.metricsService
      .getNotFoundEquipmentDetails(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          this.notFoundEquipmentDetails =
            result.data?.getNotFoundEquipmentDetails;
          this.isLoading = false;
          // Auto-expand first location if there's data
          if (this.notFoundEquipmentDetails?.byLocation?.length > 0) {
            this.expandedLocations.add(
              this.notFoundEquipmentDetails.byLocation[0].locationNum,
            );
          }
          // Create treemap chart after data loads
          setTimeout(() => {
            this.createTreemapChart();
          }, 100);
        },
        error: (error) => {
          console.error('Error loading not found equipment details:', error);
          this.error = 'Failed to load not found equipment details';
          this.isLoading = false;
        },
      });
  }

  /**
   * Toggle location expansion
   */
  toggleLocation(locationNum: string): void {
    if (this.expandedLocations.has(locationNum)) {
      this.expandedLocations.delete(locationNum);
    } else {
      this.expandedLocations.add(locationNum);
    }
  }

  /**
   * Check if location is expanded
   */
  isLocationExpanded(locationNum: string): boolean {
    return this.expandedLocations.has(locationNum);
  }

  /**
   * Format currency values for display
   */
  formatCurrency(value: number): string {
    if (value >= 1000000) {
      return '$' + (value / 1000000).toFixed(1) + 'M';
    } else if (value >= 1000) {
      return '$' + Math.round(value / 1000) + 'K';
    }
    return '$' + value.toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  /**
   * Format count values for display
   */
  formatCount(value: number): string {
    if (value >= 1000) {
      const kValue = value / 1000;
      if (kValue % 1 === 0) {
        return Math.round(kValue) + 'K';
      }
      return kValue.toFixed(1) + 'K';
    }
    return value.toLocaleString();
  }

  /**
   * Get percentage of location's value relative to total based on display mode
   */
  getLocationPercentage(location: LostEquipmentLocationDetail): number {
    if (!this.notFoundEquipmentDetails) {
      return 0;
    }

    if (this.displayMode === 'bookValue') {
      if (this.notFoundEquipmentDetails.totalBookValue === 0) {
        return 0;
      }
      return (
        (location.totalBookValue /
          this.notFoundEquipmentDetails.totalBookValue) *
        100
      );
    } else {
      if (this.notFoundEquipmentDetails.totalReplacementCost === 0) {
        return 0;
      }
      return (
        (location.totalReplacementCost /
          this.notFoundEquipmentDetails.totalReplacementCost) *
        100
      );
    }
  }

  /**
   * Get percentage of category's value relative to total based on display mode
   */
  getCategoryPercentage(category: LostEquipmentCategoryDetail): number {
    if (!this.notFoundEquipmentDetails) {
      return 0;
    }

    if (this.displayMode === 'bookValue') {
      if (this.notFoundEquipmentDetails.totalBookValue === 0) {
        return 0;
      }
      return (
        (category.bookValue / this.notFoundEquipmentDetails.totalBookValue) *
        100
      );
    } else {
      if (this.notFoundEquipmentDetails.totalReplacementCost === 0) {
        return 0;
      }
      return (
        (category.replacementCost /
          this.notFoundEquipmentDetails.totalReplacementCost) *
        100
      );
    }
  }

  /**
   * Get bar width for location visualization
   */
  getLocationBarWidth(location: LostEquipmentLocationDetail): string {
    const percentage = this.getLocationPercentage(location);
    return `${Math.min(percentage, 100)}%`;
  }

  /**
   * Get bar width for category visualization
   */
  getCategoryBarWidth(category: LostEquipmentCategoryDetail): string {
    const percentage = this.getCategoryPercentage(category);
    return `${Math.min(percentage, 100)}%`;
  }

  /**
   * Get color for location based on index
   * Using the specified color scheme
   */
  getLocationColor(index: number): string {
    const colors = [
      '#42A5F5', // Blue (graphs)
      '#2dd36f', // Green
      '#FDD835', // Yellow
      '#ffa726', // Orange
      '#673AB7', // Purple
      '#92949C', // Gray
      '#0e4aff', // Blue (metrics)
      '#ff3d71', // Red
    ];
    return colors[index % colors.length];
  }

  /**
   * Get color for category based on category name to match treemap colors
   * This ensures the same category gets the same color in both visualizations
   */
  getCategoryColor(index: number): string {
    if (!this.notFoundEquipmentDetails?.byCategory?.length) {
      return this.treemapColors[0];
    }

    // Get the sorted categories data (same as used in treemap)
    const sortedCategories = [...this.notFoundEquipmentDetails.byCategory].sort(
      (a, b) => {
        if (this.displayMode === 'bookValue') {
          return b.bookValue - a.bookValue;
        } else {
          return b.replacementCost - a.replacementCost;
        }
      },
    );

    // Get the category at the current index
    const topCategories = this.getTopCategories();
    const currentCategory = topCategories[index];

    if (!currentCategory) {
      return this.treemapColors[0];
    }

    // Find the position of this category in the sorted treemap data
    const treemapIndex = sortedCategories.findIndex(
      (cat) => cat.category === currentCategory.category,
    );

    // Use the same color mapping as treemap
    return this.treemapColors[treemapIndex % this.treemapColors.length];
  }

  /**
   * Format age in years
   */
  formatAge(value: number): string {
    if (value === 0) return '0 YR';
    return Math.round(value) + ' YR';
  }

  /**
   * Get the location with the highest book value
   */
  getTopLocation(): LostEquipmentLocationDetail | null {
    if (!this.notFoundEquipmentDetails?.byLocation?.length) {
      return null;
    }
    // Data is already sorted by totalBookValue descending
    return this.notFoundEquipmentDetails.byLocation[0];
  }

  /**
   * Get the category with the highest book value
   */
  getTopCategory(): LostEquipmentCategoryDetail | null {
    if (!this.notFoundEquipmentDetails?.byCategory?.length) {
      return null;
    }
    // Data is already sorted by bookValue descending
    return this.notFoundEquipmentDetails.byCategory[0];
  }

  /**
   * Toggle between book value and replacement cost display modes
   */
  toggleDisplayMode(mode: 'bookValue' | 'replacementCost'): void {
    this.displayMode = mode;
    this.createTreemapChart();
  }

  /**
   * Create the treemap chart for categories
   */
  private createTreemapChart(): void {
    if (
      !this.treemapContainer?.nativeElement ||
      !this.notFoundEquipmentDetails?.byCategory?.length
    ) {
      return;
    }

    // Destroy existing chart
    if (this.treemapChart) {
      this.treemapChart.destroy();
    }

    // Prepare data for treemap based on display mode
    const treemapData = this.notFoundEquipmentDetails.byCategory.map(
      (item, index) => ({
        x: item.category || 'Unknown',
        y:
          this.displayMode === 'bookValue'
            ? item.bookValue
            : item.replacementCost,
      }),
    );

    // Sort data by value for better visualization
    treemapData.sort((a, b) => b.y - a.y);

    const formatCurrency = this.formatCurrency.bind(this);
    const formatCount = this.formatCount.bind(this);
    const categoryData = this.notFoundEquipmentDetails.byCategory;

    const options = {
      series: [
        {
          data: treemapData,
        },
      ],
      chart: {
        height: 280,
        type: 'treemap',
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 800,
        },
        fontFamily: 'inherit',
        background: 'transparent',
      },
      title: {
        text: undefined,
      },
      colors: this.treemapColors,
      plotOptions: {
        treemap: {
          distributed: true,
          enableShades: false,
          shadeIntensity: 0,
          reverseNegativeShade: false,
          colorScale: {
            ranges: [],
          },
          borderRadius: 8,
          dataLabels: {
            format: 'scale',
          },
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '14px',
          fontWeight: '500',
          colors: ['#fff'],
          fontFamily: 'inherit',
        },
        formatter: function (text: string, op: any) {
          const value = op.value;
          const category = op.w.config.series[0].data[op.dataPointIndex].x;
          const originalItem = categoryData.find(
            (c) => c.category === category,
          );

          let formattedValue: string;
          let unitCount = '';

          formattedValue = formatCurrency(value);
          if (originalItem) {
            unitCount = `(${formatCount(originalItem.count)} units)`;
          }

          // Return category name on first line, value and count on separate lines
          return [category, formattedValue, unitCount];
        },
        offsetY: 0,
        textAnchor: 'middle',
        distributed: false,
      },
      tooltip: {
        enabled: true,
        custom: function ({ series, seriesIndex, dataPointIndex, w }: any) {
          const data = w.config.series[0].data[dataPointIndex];
          const originalItem = categoryData.find((c) => c.category === data.x);

          const costValue = originalItem ? originalItem.bookValue : 0;
          const countValue = originalItem ? originalItem.count : 0;

          const replacementCostValue = originalItem
            ? originalItem.replacementCost
            : 0;

          return `
            <div style="padding: 12px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.15); font-family: inherit;">
              <div style="font-weight: 600; margin-bottom: 8px; color: #304758; font-size: 14px;">
                ${data.x}
              </div>
              <div style="margin-bottom: 4px; font-size: 13px;">
                <span style="color: #666;">Book Value:</span>
                <span style="font-weight: 600; color: #EA4335; margin-left: 8px;">${formatCurrency(costValue)}</span>
              </div>
              <div style="margin-bottom: 4px; font-size: 13px;">
                <span style="color: #666;">Replacement Cost:</span>
                <span style="font-weight: 600; color: #FF6B35; margin-left: 8px;">${formatCurrency(replacementCostValue)}</span>
              </div>
              <div style="font-size: 13px;">
                <span style="color: #666;">Units:</span>
                <span style="font-weight: 600; color: #4285F4; margin-left: 8px;">${formatCount(countValue)}</span>
              </div>
            </div>
          `;
        },
      },
      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: 180,
            },
            dataLabels: {
              style: {
                fontSize: '11px',
              },
            },
          },
        },
      ],
    };

    this.treemapChart = new ApexCharts(
      this.treemapContainer.nativeElement,
      options,
    );
    this.treemapChart.render();
  }

  /**
   * Get top categories for the "Top Categories" section
   */
  getTopCategories(): LostEquipmentCategoryDetail[] {
    if (!this.notFoundEquipmentDetails?.byCategory?.length) {
      return [];
    }
    // Return all categories sorted by the current display mode value
    return [...this.notFoundEquipmentDetails.byCategory].sort((a, b) => {
      if (this.displayMode === 'bookValue') {
        return b.bookValue - a.bookValue;
      } else {
        return b.replacementCost - a.replacementCost;
      }
    });
  }

  /**
   * Close the dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}
